/**
 * Word-level Myers diff.
 *
 * Every redline decision in the engine starts here. The paragraph text and the
 * requested text are tokenised into words and this module returns the shortest
 * edit script between the two word lists. Diffing words rather than characters
 * is what keeps a tracked change readable: Word shows "diligent" struck as a
 * whole, never "dil" struck and "x" inserted inside a word.
 *
 * The result covers both inputs end to end. In every op `beforeStart..beforeEnd`
 * indexes the `before` list and `afterStart..afterEnd` the `after` list, both
 * half-open. Adjacent ops with the same tag are merged, so a caller can treat
 * each op as one hunk.
 *
 * `wordDiff` stays a function by decision: it is a transcription of the 1986
 * algorithm, and inside `core` the locals keep the paper's names (`n`, `m`,
 * `v`, `k`, `d`, `x`, `y`) so the code can be read against it.
 */
export type WordDiffHunkTag = "equal" | "replace" | "delete" | "insert";

export interface WordDiffHunk {
    tag: WordDiffHunkTag;
    beforeStart: number;
    beforeEnd: number;
    afterStart: number;
    afterEnd: number;
}

/**
 * Entry point. Strips the shared head and tail, runs the forward search of
 * Myers' 1986 algorithm on the middle, and shifts the result back.
 *
 * A legal edit typically changes a handful of words in a paragraph of
 * hundreds, so the search only ever runs on the small middle section. The
 * search is O((N + M) * D) in the number of differences, which makes this
 * near-linear in practice and keeps the live proposal responsive.
 */
export function wordDiff(before: string[], after: string[]): WordDiffHunk[] {
    /** A hunk with no `before` words is an insert, one with no `after` words a delete; anything else replaces. */
    const tagNonEqualRangePair = (nonEqualRangePair: Omit<WordDiffHunk, "tag">): WordDiffHunk => {
        if (nonEqualRangePair.beforeStart === nonEqualRangePair.beforeEnd) return { tag: "insert", ...nonEqualRangePair };
        /**
         * WHEN `afterStart === afterEnd`
         *   CONSEQUENCE
         *     The gap has no surviving `after` words, so classify it as a delete.
         *
         * EXAMPLE
         *   before range = 4..7
         *   after range  = 2..2
         *   deleted      = before indexes 4, 5, 6
         */
        if (nonEqualRangePair.afterStart === nonEqualRangePair.afterEnd) return { tag: "delete", ...nonEqualRangePair };
        return { tag: "replace", ...nonEqualRangePair };
    };
    /**
     * Coalesces neighbouring ops of the same tag. The search emits one op per
     * word, and the engine wants "three words replaced by two" as a single hunk
     * so the whole phrase is struck and reinserted together.
     */
    const mergeAdjacentOps = (reassembledOps: WordDiffHunk[]): WordDiffHunk[] => {
        const mergedOps: WordDiffHunk[] = [];
        for (const op of reassembledOps) {
            /**
             * EXAMPLE
             *   mergedOps.length = 3  -> index 2, the last merged op
             *   mergedOps.length = 0  -> index -1, which reads as `undefined`
             *
             * CONSEQUENCE
             *   The first op has no previous neighbour, so it skips merge logic.
             */
            const previousMergedOp = mergedOps[mergedOps.length - 1];
            /**
             * Whether this op extends the one merged before it: same kind, and both
             * ranges continue where the previous op's ended.
             *
             * CASES
             *   - nothing merged yet                     -> no: the first op opens the list
             *   - a different kind (insert after delete) -> no: they stay separate hunks
             *   - a gap in the before range              -> no: an equal run sits between them
             *   - otherwise                              -> yes when the after range is contiguous too
             */
            const previousMergedOpCanBeCompared = (): boolean => {
                const previousMergedOpExists = previousMergedOp !== undefined;
                if (!previousMergedOpExists) return false;
                const previousMergedOpHasSameTag = previousMergedOp.tag === op.tag;
                if (!previousMergedOpHasSameTag) return false;
                const previousMergedOpTouchesBeforeRangeStart = previousMergedOp.beforeEnd === op.beforeStart;
                if (!previousMergedOpTouchesBeforeRangeStart) return false;
                return previousMergedOp.afterEnd === op.afterStart;
            };
            /**
             * WHEN the previous op exists, has the same tag, and both ranges touch
             *   CONSEQUENCE
             *     Treat the two ops as one continuous change and extend the previous op.
             *
             * EXAMPLE
             *   first  = replace before 2..4 / after 2..3
             *   next   = replace before 4..5 / after 3..4
             *   merged = replace before 2..5 / after 2..4
             */
            if (previousMergedOpCanBeCompared()) {
                previousMergedOp.beforeEnd = op.beforeEnd;
                previousMergedOp.afterEnd = op.afterEnd;
                /**
                 * CONSEQUENCE
                 *   The current op was absorbed into `previousMergedOp`, so do not push a second op.
                 */
                continue;
            }
            mergedOps.push({ ...op });
        }
        return mergedOps;
    };

    const diffTrimmedMiddleWords = (left: string[], right: string[]): WordDiffHunk[] => {
        /**
         * Recovers the edit path from the frontier snapshots saved during the
         * search. Walking from the final `editDistance` back to 1, each step picks the
         * diagonal the frontier was extended from, records the diagonal
         * (matching) moves as equal pairs, and steps once over the single insert
         * or delete. Every gap between consecutive matches becomes one hunk.
         */
        /**
         * TYPE
         *   `diagonalIndexOffset` is the array cell for diagonal 0.
         *
         * EXAMPLE
         *   maxEditDistance   = 5
         *   legal diagonals   = -5..5
         *   snapshot index    = diagonalIndexOffset + k
         *   mapped cells      = 0..10
         */
        const reconstructOpsFromFrontierSnapshots = (frontierSnapshotsByEditDistance: Int32Array[], diagonalIndexOffset: number): WordDiffHunk[] => {
            /**
             * TYPE
             *   Each entry is `[beforeWordIndex, afterWordIndex]` for one equal-word match.
             *
             * EXAMPLE
             *   [3, 2] -> `left[3] === right[2]`
             */
            const matchedWordIndexPairs: [number, number][] = [];
            let beforeIndex = left.length;
            let afterIndex = right.length;

            /**
             * EXAMPLE
             *   snapshots length = 5
             *   valid indexes    = 0..4
             *   replay starts at = 3
             *   replay walks      3, 2, 1
             *
             * WHY
             *   The array holds one snapshot before each layer plus one final snapshot after
             *   success. Index 0 is left for the final all-equal run back to the origin.
             */
            for (let editDistance = frontierSnapshotsByEditDistance.length - 2; editDistance > 0; editDistance--) {
                const furthestXByDiagonal = frontierSnapshotsByEditDistance[editDistance];
                /**
                 * TYPE
                 *   The diagonal of the current path endpoint.
                 *
                 * WHY
                 *   Every point on diagonal `k` satisfies `beforeIndex - afterIndex = k`. The saved
                 *   frontier stores the furthest reachable `beforeIndex` on each diagonal after the
                 *   snake.
                 */
                const currentDiagonal = beforeIndex - afterIndex;
                /**
                 * CASES
                 *   - came from `currentDiagonal + 1` -> last edit was an insert on the `after` side
                 *   - came from `currentDiagonal - 1` -> last edit was a delete on the `before` side
                 *
                 * EXAMPLE
                 *   editDistance    = 2
                 *   currentDiagonal = 0
                 *   compare source diagonals -1 and 1 to choose the prior frontier
                 */
                const sourceDiagonal =
                    currentDiagonal === -editDistance || (currentDiagonal !== editDistance && furthestXByDiagonal[diagonalIndexOffset + currentDiagonal - 1] < furthestXByDiagonal[diagonalIndexOffset + currentDiagonal + 1]) ? currentDiagonal + 1 : currentDiagonal - 1;
                const previousX = furthestXByDiagonal[diagonalIndexOffset + sourceDiagonal];
                const previousY = previousX - sourceDiagonal;
                /**
                 * Whether the backtrack is still inside the diagonal run (the "snake") of
                 * equal words that led from the previous frontier point to this one.
                 *
                 * CASES
                 *   - beforeIndex has reached the previous frontier's x -> no: the snake is consumed
                 *   - otherwise                                          -> yes while afterIndex is past its y too
                 */
                const matchedSnakeCanContinue = (): boolean => {
                    const beforeIndexRemainsPastPreviousX = beforeIndex > previousX;
                    if (!beforeIndexRemainsPastPreviousX) return false;
                    return afterIndex > previousY;
                };

                /**
                 * EXAMPLE
                 *   beforeIndex, afterIndex = 5, 4
                 *   previousX, previousY    = 3, 2
                 *   pushes                  = [4, 3], then [3, 2]
                 *
                 * WHY
                 *   The pre-decrements land on the matched words inside the snake, not on the
                 *   boundary point after them.
                 */
                while (matchedSnakeCanContinue()) {
                    const matchedBeforeWordIndex = --beforeIndex;
                    const matchedAfterWordIndex = --afterIndex;
                    const matchedWordIndexPair = [matchedBeforeWordIndex, matchedAfterWordIndex] as [number, number];
                    matchedWordIndexPairs.push(matchedWordIndexPair);
                }
                /**
                 * WHEN `beforeIndex > previousX` after consuming the snake
                 *   CONSEQUENCE
                 *     The backtracked edit was a delete, so move left over the extra `before` word.
                 */
                if (beforeIndex > previousX) beforeIndex--;
                /**
                 * WHEN the delete case did not apply
                 *   CONSEQUENCE
                 *     The backtracked edit was an insert, so move up over the extra `after` word.
                 */
                else afterIndex--;
            }
            // Whatever is left at d = 0 is a pure run of matches back to the origin.
            /**
             * CASES
             *   - the before side is exhausted -> no: any after words left are a leading insertion
             *   - otherwise                    -> yes while the after side has words left too
             */
            const matchedRunToOriginCanContinue = (): boolean => {
                const beforeIndexRemainsAboveOrigin = beforeIndex > 0;
                if (!beforeIndexRemainsAboveOrigin) return false;
                return afterIndex > 0;
            };
            while (matchedRunToOriginCanContinue()) {
                const matchedBeforeWordIndex = --beforeIndex;
                const matchedAfterWordIndex = --afterIndex;
                const matchedWordIndexPair = [matchedBeforeWordIndex, matchedAfterWordIndex] as [number, number];
                matchedWordIndexPairs.push(matchedWordIndexPair);
            }
            matchedWordIndexPairs.reverse();

            const reconstructedOps: WordDiffHunk[] = [];
            let nextUnmatchedBeforeIndex = 0;
            let nextUnmatchedAfterIndex = 0;
            for (const [matchedBeforeIndex, matchedAfterIndex] of matchedWordIndexPairs) {
                const matchedBeforeIndexLeavesUnmatchedBeforeGap = matchedBeforeIndex > nextUnmatchedBeforeIndex;
                const matchedAfterIndexLeavesUnmatchedAfterGap = matchedAfterIndex > nextUnmatchedAfterIndex;
                const matchedPairLeavesUnmatchedGap = matchedBeforeIndexLeavesUnmatchedBeforeGap || matchedAfterIndexLeavesUnmatchedAfterGap;
                /**
                 * WHEN either side advanced past the next unmatched cursor before this match
                 *   CONSEQUENCE
                 *     Emit one non-equal hunk for the gap before the recovered match.
                 */
                if (matchedPairLeavesUnmatchedGap) {
                    reconstructedOps.push(tagNonEqualRangePair({ beforeStart: nextUnmatchedBeforeIndex, beforeEnd: matchedBeforeIndex, afterStart: nextUnmatchedAfterIndex, afterEnd: matchedAfterIndex }));
                }
                /**
                 * EXAMPLE
                 *   match       = (6, 4)
                 *   emitted op  = before 6..7 / after 4..5
                 *
                 * WHY
                 *   Each `+ 1` makes a one-word half-open range.
                 */
                reconstructedOps.push({ tag: "equal", beforeStart: matchedBeforeIndex, beforeEnd: matchedBeforeIndex + 1, afterStart: matchedAfterIndex, afterEnd: matchedAfterIndex + 1 });
                /**
                 * EXAMPLE
                 *   equal op uses before 6..7
                 *   next gap starts at before index 7
                 */
                nextUnmatchedBeforeIndex = matchedBeforeIndex + 1;
                /**
                 * EXAMPLE
                 *   equal op uses after 4..5
                 *   next gap starts at after index 5
                 */
                nextUnmatchedAfterIndex = matchedAfterIndex + 1;
            }
            const beforeWordsRemainUnmatched = nextUnmatchedBeforeIndex < left.length;
            const afterWordsRemainUnmatched = nextUnmatchedAfterIndex < right.length;
            const unmatchedWordsRemain = beforeWordsRemainUnmatched || afterWordsRemainUnmatched;
            /**
             * WHEN either side still has unmatched words after the last recovered match
             *   CONSEQUENCE
             *     Emit one trailing non-equal hunk to cover the remaining tail.
             */
            if (unmatchedWordsRemain) {
                reconstructedOps.push(tagNonEqualRangePair({ beforeStart: nextUnmatchedBeforeIndex, beforeEnd: left.length, afterStart: nextUnmatchedAfterIndex, afterEnd: right.length }));
            }
            return mergeAdjacentOps(reconstructedOps);
        };

        const n = left.length;
        const m = right.length;
        const beforeWordCountIsZero = n === 0;
        const afterWordCountIsZero = m === 0;
        const bothWordListsAreEmpty = beforeWordCountIsZero && afterWordCountIsZero;
        /**
         * WHEN both token lists are empty
         *   CONSEQUENCE
         *     Return no ops.
         */
        if (bothWordListsAreEmpty) return [];
        /**
         * WHEN `n === 0`
         *   CONSEQUENCE
         *     The whole diff is one insert covering `after 0..m`.
         */
        if (n === 0) return [tagNonEqualRangePair({ beforeStart: 0, beforeEnd: 0, afterStart: 0, afterEnd: m })];
        /**
         * TYPE
         *   `furthestXByDiagonal` stores, for each diagonal `k = x - y`, the furthest `x`
         *   reached after the layer's edit and the following snake.
         *
         * EXAMPLE
         *   maxEditDistance    = 5
         *   diagonalIndexOffset = 5
         *   diagonal -5        -> cell 0
         *   diagonal 0         -> cell 5
         *   diagonal 5         -> cell 10
         *
         * WHY
         *   `frontierSnapshotsByEditDistance` keeps this same mapping at each layer for
         *   backtracking.
         */
        if (m === 0) return [tagNonEqualRangePair({ beforeStart: 0, beforeEnd: n, afterStart: 0, afterEnd: 0 })];

        const maxEditDistance = n + m;
        const diagonalIndexOffset = maxEditDistance;
        /**
         * EXAMPLE
         *   maxEditDistance = 3
         *   array length    = 7
         *   diagonals       = -3, -2, -1, 0, 1, 2, 3
         *   cell indexes    = 0..6
         */
        const furthestXByDiagonal = new Int32Array(2 * maxEditDistance + 1);
        const frontierSnapshotsByEditDistance: Int32Array[] = [];

        for (let d = 0; d <= maxEditDistance; d++) {
            /**
             * WHEN saving the snapshot before processing layer `d`
             *   CONSEQUENCE
             *     Backtracking can later decide which neighbouring diagonal led into this
             *     layer from the exact pre-layer frontier.
             */
            frontierSnapshotsByEditDistance.push(furthestXByDiagonal.slice());
            /**
             * CASES
             *   - `diagonal === -d`                                       -> must come from `k + 1`
             *   - `diagonal === d`                                        -> must come from `k - 1`
             *   - lower neighbour reached further than upper neighbour    -> come from `k + 1`
             *   - otherwise                                               -> come from `k - 1`
             *
             * CONSEQUENCE
             *   `k + 1` means an insert on the `after` side; `k - 1` means a delete on the
             *   `before` side.
             */
            for (let diagonal = -d; diagonal <= d; diagonal += 2) {
                const diagonalIsLowestInLayer = diagonal === -d;
                const diagonalIsNotHighestInLayer = diagonal !== d;
                const upperSourceDiagonalReachedFurther = diagonalIsNotHighestInLayer && furthestXByDiagonal[diagonalIndexOffset + diagonal - 1] < furthestXByDiagonal[diagonalIndexOffset + diagonal + 1];
                const pathComesFromUpperSourceDiagonal = diagonalIsLowestInLayer || upperSourceDiagonalReachedFurther;
                /**
                 * EXAMPLE
                 *   source diagonal `k - 1` reached beforeIndex 4
                 *   delete step moves to beforeIndex 5 on diagonal `k`
                 *
                 * WHY
                 *   The `+ 1` consumes one deleted `before` word before the snake starts.
                 */
                let beforeIndex =
                    pathComesFromUpperSourceDiagonal
                        ? furthestXByDiagonal[diagonalIndexOffset + diagonal + 1]
                        : furthestXByDiagonal[diagonalIndexOffset + diagonal - 1] + 1;
                /**
                 * WHEN the edit step for this layer has been chosen
                 *   CONSEQUENCE
                 *     Advance only through equal words. When the loop stops, the stored
                 *     `beforeIndex` is the furthest point reachable on this diagonal for this
                 *     edit distance.
                 */
                let afterIndex = beforeIndex - diagonal;
                /**
                 * Whether the diagonal run of equal words can take one more step.
                 *
                 * CASES
                 *   - the left text is exhausted  -> no
                 *   - the right text is exhausted -> no
                 *   - otherwise                   -> yes while the two next words are equal
                 */
                const equalWordSnakeCanContinue = (): boolean => {
                    const beforeIndexRemainsInsideLeft = beforeIndex < n;
                    if (!beforeIndexRemainsInsideLeft) return false;
                    const afterIndexRemainsInsideRight = afterIndex < m;
                    if (!afterIndexRemainsInsideRight) return false;
                    return left[beforeIndex] === right[afterIndex];
                };
                while (equalWordSnakeCanContinue()) {
                    beforeIndex++;
                    afterIndex++;
                }
                furthestXByDiagonal[diagonalIndexOffset + diagonal] = beforeIndex;
                const beforeFrontierReachedTrimmedEnd = beforeIndex >= n;
                const afterFrontierReachedTrimmedEnd = afterIndex >= m;
                const frontierReachedBothTrimmedEnds = beforeFrontierReachedTrimmedEnd && afterFrontierReachedTrimmedEnd;
                /**
                 * WHEN the frontier has reached both trimmed ends
                 *   CONSEQUENCE
                 *     This layer found a complete path, so stop the search and reconstruct ops.
                 */
                if (frontierReachedBothTrimmedEnds) {
                    /**
                     * WHEN saving the frontier after the successful layer
                     *   CONSEQUENCE
                     *     Backtracking can start from the completed frontier, which is why replay
                     *     begins at `length - 2` instead of the last index.
                     */
                    frontierSnapshotsByEditDistance.push(furthestXByDiagonal.slice());
                    return reconstructOpsFromFrontierSnapshots(frontierSnapshotsByEditDistance, diagonalIndexOffset);
                }
            }
        }
        return [];
    };

    /**
     * TYPE
     *   Count of equal leading words. Also the exclusive end boundary of the shared
     *   prefix in both inputs.
     */
    let sharedPrefixWordCount = 0;
    /**
     * CASES
     *   - `before` is used up  -> no: the whole of it is the prefix
     *   - `after` is used up   -> no: the whole of it is the prefix
     *   - otherwise            -> yes while the next words are equal
     */
    const sharedPrefixCanGrow = (): boolean => {
        const sharedPrefixRemainsInsideBefore = sharedPrefixWordCount < before.length;
        if (!sharedPrefixRemainsInsideBefore) return false;
        const sharedPrefixRemainsInsideAfter = sharedPrefixWordCount < after.length;
        if (!sharedPrefixRemainsInsideAfter) return false;
        return before[sharedPrefixWordCount] === after[sharedPrefixWordCount];
    };
    /**
     * WHEN words keep matching at the current prefix boundary
     *   CONSEQUENCE
     *     Extend the shared prefix by one word. Stop at the first mismatch; any later
     *     resynchronisation belongs to the middle diff.
     */
    while (sharedPrefixCanGrow()) sharedPrefixWordCount++;

    /**
     * TYPE
     *   Count of equal trailing words after the shared prefix is excluded. This is a
     *   count, not an original-array index.
     */
    let sharedSuffixWordCount = 0;
    /**
     * CASES
     *   - the suffix would reach into the prefix on `before` -> no: a word is never both
     *   - the same on `after`                                -> no
     *   - otherwise                                          -> yes while the words counted from each end are equal
     *
     * EXAMPLE
     *   before = ["a", "b", "c", "d"], sharedPrefixWordCount = 1, sharedSuffixWordCount = 0
     *   before.length - 1 - 0 -> 3, "d", the last word; with count 1 -> 2, "c"
     *   the bound before.length - sharedPrefixWordCount = 3 stops the suffix at "b", leaving "a" to the prefix
     */
    const sharedSuffixCanGrow = (): boolean => {
        const sharedSuffixRemainsInsideBefore = sharedSuffixWordCount < before.length - sharedPrefixWordCount;
        if (!sharedSuffixRemainsInsideBefore) return false;
        const sharedSuffixRemainsInsideAfter = sharedSuffixWordCount < after.length - sharedPrefixWordCount;
        if (!sharedSuffixRemainsInsideAfter) return false;
        return before[before.length - 1 - sharedSuffixWordCount] === after[after.length - 1 - sharedSuffixWordCount];
    };
    /**
     * EXAMPLE
     *   length = 5, suffix count = 0 -> check index 4
     *   length = 5, suffix count = 1 -> check index 3
     */
    while (
        sharedSuffixCanGrow()
    ) {
        sharedSuffixWordCount++;
    }
    const sharedPrefixWordCountIsZero = sharedPrefixWordCount === 0;
    const sharedSuffixWordCountIsZero = sharedSuffixWordCount === 0;
    const noSharedPrefixOrSuffixWasStripped = sharedPrefixWordCountIsZero && sharedSuffixWordCountIsZero;

    /**
     * WHEN neither a shared prefix nor a shared suffix was stripped
     *   CONSEQUENCE
     *     Diff the whole inputs directly.
     */
    if (noSharedPrefixOrSuffixWasStripped) return diffTrimmedMiddleWords(before, after);
    const trimmedMiddleBeforeWords = before.slice(sharedPrefixWordCount, before.length - sharedSuffixWordCount);
    const trimmedMiddleAfterWords = after.slice(sharedPrefixWordCount, after.length - sharedSuffixWordCount);

    /**
     * EXAMPLE
     *   before.length         = 10
     *   sharedPrefixWordCount = 2
     *   sharedSuffixWordCount = 3
     *   before.slice(2, 7)    -> keeps indexes 2, 3, 4, 5, 6
     */
    const middleOps = diffTrimmedMiddleWords(trimmedMiddleBeforeWords, trimmedMiddleAfterWords);
    /**
     * WHY
     *   `middleOps` indexes are relative to the trimmed slices. Adding
     *   `sharedPrefixWordCount` re-anchors each boundary in the original inputs. The
     *   stripped suffix needs no extra shift because the middle indexes already stop
     *   before it.
     */
    const middleOpsShiftedToOriginalIndexes = middleOps.map((op) => ({
        tag: op.tag,
        /**
         * EXAMPLE
         *   middle starts at original before index 2
         *   trimmed `beforeStart` = 1
         *   original `beforeStart` = 3
         */
        beforeStart: op.beforeStart + sharedPrefixWordCount,
        /**
         * EXAMPLE
         *   middle starts at original before index 2
         *   trimmed `beforeEnd`   = 4
         *   original `beforeEnd`  = 6
         */
        beforeEnd: op.beforeEnd + sharedPrefixWordCount,
        /**
         * EXAMPLE
         *   middle starts at original after index 2
         *   trimmed `afterStart`  = 0
         *   original `afterStart` = 2
         */
        afterStart: op.afterStart + sharedPrefixWordCount,
        /**
         * EXAMPLE
         *   middle starts at original after index 2
         *   trimmed `afterEnd`    = 3
         *   original `afterEnd`   = 5
         */
        afterEnd: op.afterEnd + sharedPrefixWordCount,
    }));
    const reassembledOps: WordDiffHunk[] = [];
    /**
     * WHEN `sharedPrefixWordCount > 0`
     *   CONSEQUENCE
     *     Reinsert the stripped leading equal run as one contiguous `equal` op.
     */
    if (sharedPrefixWordCount > 0) reassembledOps.push({ tag: "equal", beforeStart: 0, beforeEnd: sharedPrefixWordCount, afterStart: 0, afterEnd: sharedPrefixWordCount });
    reassembledOps.push(...middleOpsShiftedToOriginalIndexes);
    /**
     * WHEN `sharedSuffixWordCount > 0`
     *   CONSEQUENCE
     *     Reinsert the stripped trailing equal run.
     */
    if (sharedSuffixWordCount > 0) {
        reassembledOps.push({
            tag: "equal",
            /**
             * EXAMPLE
             *   before.length         = 9
             *   sharedSuffixWordCount = 2
             *   suffix starts at      = 7
             *   restored range        = 7..9
             */
            beforeStart: before.length - sharedSuffixWordCount,
            beforeEnd: before.length,
            /**
             * EXAMPLE
             *   after.length          = 8
             *   sharedSuffixWordCount = 2
             *   suffix starts at      = 6
             *   restored range        = 6..8
             */
            afterStart: after.length - sharedSuffixWordCount,
            afterEnd: after.length,
        });
    }
    /**
     * WHY
     *   Reassembly can create touching ops with the same tag at the prefix/middle or
     *   middle/suffix boundary. Merge them so each contiguous hunk is returned once.
     *
     * EXAMPLE
     *   middle equal  = 2..5
     *   suffix starts = 5
     *   result        -> one equal span, not two touching equal ops
     */
    return mergeAdjacentOps(reassembledOps);
}
